import { useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  fetchCityRankedVenues,
  fetchRatedCities,
  fetchVenueRankContext,
  formatScore,
  searchRatedVenues,
  venueDisplayName,
  type GrowthVenue,
} from '../lib/growth';

/**
 * Live-data bridge for `public/growth/social-studio.html`.
 *
 * The studio is a STATIC file in `public/` — it has no Supabase key and must not get one (it is
 * served unauthenticated and is also opened standalone). So it never queries: it posts a request,
 * this component answers with the admin's own session, and the studio only ever receives rows.
 * That is the same division the App Store series editor implies — the iframe owns rendering, React
 * owns data — just made two-way.
 *
 * Protocol (both directions carry `ns: 'shado-studio'`, and both ends check `origin`):
 *   studio → admin   ready | reqCityVenues{cityId} | reqVenueSearch{q} | reqVenueDetail{venueId}
 *   admin  → studio  data{totals,cities} | cityVenues{cityId,venues} | venueSearch{venues}
 *                    | venueDetail{venueId,detail}
 *
 * Nothing here mutates: every call is a SELECT.
 */

type Msg = {
  ns?: string;
  type?: string;
  cityId?: string;
  q?: string;
  venueId?: string;
  /* aiPost — the Venue Post Studio's copilot request. */
  prompt?: string;
  slots?: Record<string, string>;
  venue?: string;
  city?: string;
  template?: string;
  /* saveAsset — the studio hands its draft up; this component writes it with the admin session. */
  title?: string;
  studio?: string;
  width?: number;
  height?: number;
  payload?: unknown;
};

/** A library draft handed back into a studio (see StudioScreen's Library tab). */
export type StudioDraft = {
  studio: string;
  tpl: string;
  label?: string;
  html: string;
  theme?: string;
  qr?: unknown;
  cw?: number;
  ch?: number;
  thumb?: string;
};

const ORIGIN_ANY = '*';

/** `logs` columns → the five display criteria. Mirrors `buildRatingPayload` in the app's
 *  EditRatingModal: bar = …/Service, club & event = …/Production (`lineup`). */
const CRITERIA_BY_TYPE: Record<'bar' | 'club', { label: string; col: string }[]> = {
  bar: [
    { label: 'Venue', col: 'venue_score' },
    { label: 'Atmosphere', col: 'atmosphere' },
    { label: 'Music', col: 'music' },
    { label: 'Drinks', col: 'drinks' },
    { label: 'Service', col: 'service' },
  ],
  club: [
    { label: 'Venue', col: 'venue_score' },
    { label: 'Atmosphere', col: 'atmosphere' },
    { label: 'Music', col: 'music' },
    { label: 'Drinks', col: 'drinks' },
    { label: 'Production', col: 'lineup' },
  ],
};

type LogRow = Record<string, unknown>;

function avg(rows: LogRow[], col: string): number | null {
  const vals = rows
    .map((r) => Number(r[col]))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** `logs.peak_time` is an hour-of-night float that runs PAST 24 for after-midnight (26.5 = 2:30 AM). */
function formatPeak(hour: number | null): string {
  if (hour == null || !Number.isFinite(hour)) return '';
  const h24 = ((hour % 24) + 24) % 24;
  const h = Math.floor(h24);
  const m = Math.round((h24 - h) * 60);
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(Math.min(59, Math.max(0, m))).padStart(2, '0')} ${suffix}`;
}

/** Thought tags across every log at the venue, most-used first — the same shape the venue page shows. */
function topTags(rows: LogRow[], limit = 8): string[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const raw = r.criteria_thought_tags;
    if (!raw || typeof raw !== 'object') continue;
    for (const list of Object.values(raw as Record<string, unknown>)) {
      if (!Array.isArray(list)) continue;
      for (const t of list) {
        const tag = String(t ?? '').trim();
        if (!tag) continue;
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    }
  }
  const entries: [string, number][] = [];
  counts.forEach((n, tag) => entries.push([tag, n]));
  return entries
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map((e) => e[0]);
}

function venueSub(v: GrowthVenue): string {
  return [v.locality, v.venue_type].filter(Boolean).join(' · ');
}

async function countOf(q: PromiseLike<{ count: number | null }>): Promise<number> {
  try {
    const { count } = await q;
    return count ?? 0;
  } catch {
    return 0;
  }
}

async function loadTotals() {
  const [users, logs, venuesRated, citiesRated] = await Promise.all([
    /**
     * ⚠️ THE USER COUNT EXCLUDES ACCOUNTS THAT NEVER FINISHED SIGNING UP (Evan, 2026-10-01:
     * *"make sure those are not counting as users in the admin dash."*).
     *
     * It used to be a bare `profiles` count, which included every abandoned signup: a row is
     * written BEFORE phone verification (GoTrue needs a user to attach a phone to), so somebody
     * who bounced at the OTP screen counted as a user. Measured 2026-10-01: 29 of them.
     *
     * ⚠️ IT HAS TO BE AN RPC. "Did they finish" lives in `auth.users.phone_confirmed_at`, which no
     * client key can read, and the one column on `profiles` that tracks it (`phone`) has had its
     * SELECT privilege deliberately revoked — a `.not('phone','is',null)` filter would 42501
     * rather than filter. `admin_user_totals()` is SECURITY DEFINER and admin-gated.
     *
     * ⚠️ FALLS BACK TO THE OLD COUNT rather than to zero. If the RPC is missing or the caller is
     * not an admin, an inflated number is a far better failure than a dashboard confidently
     * reporting no users.
     */
    (async () => {
      try {
        const { data, error } = await supabase.rpc('admin_user_totals');
        const confirmed = (data as { confirmed?: number } | null)?.confirmed;
        if (!error && typeof confirmed === 'number') return confirmed;
      } catch {
        /* fall through to the unfiltered count below */
      }
      return countOf(supabase.from('profiles').select('id', { count: 'exact', head: true }));
    })(),
    countOf(
      supabase
        .from('logs')
        .select('id', { count: 'exact', head: true })
        .is('retired_at', null)
        .is('deleted_at', null),
    ),
    countOf(
      supabase.from('venues').select('id', { count: 'exact', head: true }).gte('venue_shado_unique_users', 1),
    ),
    countOf(supabase.from('cities').select('id', { count: 'exact', head: true }).gt('total_shado_ratings', 0)),
  ]);
  return { users, logs, venuesRated, citiesRated };
}

async function loadVenueDetail(venueId: string) {
  const { data: vRow } = await supabase
    .from('venues')
    .select(
      'id, venue_name_user_input, osm_name, venue_type, venue_shado_score, venue_shado_unique_users, venue_shado_rating_count, fsq_popularity, city_id, locality, is_hidden',
    )
    .eq('id', venueId)
    .maybeSingle();
  if (!vRow) return null;
  const venue = vRow as GrowthVenue;

  const [ctx, logRes, slugRes] = await Promise.all([
    fetchVenueRankContext(venue),
    supabase
      .from('logs')
      .select('venue_score, atmosphere, music, drinks, service, lineup, peak_time, criteria_thought_tags')
      .eq('venue_id', venueId)
      .is('retired_at', null)
      .is('deleted_at', null)
      .limit(400),
    /* the venue's public-page slug (venue.joinshado.com/<slug>) — RLS-readable, null when unpublished */
    supabase.from('venue_public_pages').select('slug').eq('venue_id', venueId).maybeSingle(),
  ]);
  const publicSlug = (slugRes.data as { slug?: string } | null)?.slug ?? null;

  const rows = ((logRes.data ?? []) as LogRow[]) ?? [];
  const kind: 'bar' | 'club' = venue.venue_type === 'bar' ? 'bar' : 'club';
  const criteria = CRITERIA_BY_TYPE[kind].map((c) => {
    const a = avg(rows, c.col);
    return { label: c.label, value: a == null ? '' : a.toFixed(1) };
  });

  return {
    name: venueDisplayName(venue),
    city: ctx.cityName ?? venue.locality ?? '',
    type: venue.venue_type ?? '',
    score: formatScore(venue.venue_shado_score),
    raters: venue.venue_shado_unique_users ?? 0,
    rank: ctx.rank,
    criteria,
    tags: topTags(rows),
    peak: formatPeak(avg(rows, 'peak_time')),
    publicUrl: publicSlug ? `https://venue.joinshado.com/${publicSlug}` : null,
  };
}

export function StudioDataBridge({
  iframeRef,
  draft,
  onDraftDelivered,
}: {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** When set, delivered as `loadDraft` the moment the studio says `ready`. */
  draft?: StudioDraft | null;
  onDraftDelivered?: () => void;
}) {
  useEffect(() => {
    let alive = true;

    const post = (payload: Record<string, unknown>) => {
      const win = iframeRef.current?.contentWindow;
      if (!win || !alive) return;
      // Same-origin in practice; `*` keeps it working behind Vercel preview aliases where the
      // iframe's resolved origin can differ from `window.location.origin`. Nothing secret is sent.
      win.postMessage({ ns: 'shado-studio', ...payload }, ORIGIN_ANY);
    };

    const onMessage = async (e: MessageEvent) => {
      const win = iframeRef.current?.contentWindow;
      if (!win || e.source !== win) return;
      const msg = e.data as Msg | null;
      if (!msg || msg.ns !== 'shado-studio') return;

      try {
        if (msg.type === 'ready') {
          const [totals, cityRows] = await Promise.all([loadTotals(), fetchRatedCities(120)]);
          post({
            type: 'data',
            totals,
            cities: cityRows.map((c) => ({
              id: c.id,
              name: c.name,
              region: c.region,
              country: c.country_code,
              ratings: c.total_shado_ratings ?? 0,
              tier: c.tier,
            })),
          });
          if (draft) {
            post({ type: 'loadDraft', draft });
            onDraftDelivered?.();
          }
        } else if (msg.type === 'saveAsset' && msg.payload) {
          /* The one write on this bridge. `prompt` carries the discriminator so these rows never
             render as HTML in the Make-anything library, and the Library tab can filter TO them. */
          const { error } = await supabase.rpc('admin_save_studio_asset', {
            p_title: msg.title || 'Untitled design',
            p_prompt: `studio-draft:${msg.studio ?? 'unknown'}`,
            p_html: JSON.stringify(msg.payload),
            p_width: msg.width ?? 0,
            p_height: msg.height ?? 0,
          });
          post({ type: 'assetSaved', ok: !error });
        } else if (msg.type === 'reqCityVenues' && msg.cityId) {
          const venues = await fetchCityRankedVenues(msg.cityId, 10);
          post({
            type: 'cityVenues',
            cityId: msg.cityId,
            venues: venues.map((v) => ({
              id: v.id,
              name: venueDisplayName(v),
              sub: venueSub(v),
              score: formatScore(v.venue_shado_score),
            })),
          });
        } else if (msg.type === 'reqVenueSearch') {
          const venues = await searchRatedVenues(msg.q ?? '', 15);
          post({
            type: 'venueSearch',
            venues: venues.map((v) => ({
              id: v.id,
              name: venueDisplayName(v),
              sub: venueSub(v),
              score: formatScore(v.venue_shado_score),
            })),
          });
        } else if (msg.type === 'reqVenueDetail' && msg.venueId) {
          const detail = await loadVenueDetail(msg.venueId);
          post({ type: 'venueDetail', venueId: msg.venueId, detail });
        } else if (msg.type === 'aiPost') {
          /* Venue Post Studio copilot — slot-level copy edits via the admin-gated edge fn. */
          try {
            const { data, error } = await supabase.functions.invoke('venue-post-copilot', {
              body: {
                prompt: msg.prompt,
                slots: msg.slots,
                venue: msg.venue,
                city: msg.city,
                template: msg.template,
              },
            });
            if (error) throw new Error(error.message ?? 'AI request failed');
            post({ type: 'aiPostResult', ok: Boolean((data as { ok?: boolean })?.ok), ...(data as object) });
          } catch (e) {
            post({
              type: 'aiPostResult',
              ok: false,
              message: e instanceof Error ? e.message : 'AI request failed',
            });
          }
        }
      } catch {
        /* A failed SELECT must never break the editor — the studio keeps its hand-typed copy. */
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      alive = false;
      window.removeEventListener('message', onMessage);
    };
  }, [iframeRef, draft, onDraftDelivered]);

  return null;
}
